import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../ModalContainer/ModelContainer'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import Icon from '../../Icons'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

// Unit-level vehicle sharing changes what every resident of a unit can see and
// manage, so only property-level admins may toggle it.
const HOUSEHOLD_VEHICLE_SHARING_ROLES = ['PropertyManager', 'Administrator']

const SettingsModal = ({ onClose, valetSettings, buildingId, fetchValetSettings, userRole }) => {
  const { t } = useTranslation(['parking-dashboard', 'common'])
  const { displaySnackbar } = useSnackbar()
  const canEditHouseholdVehicleSharing = HOUSEHOLD_VEHICLE_SHARING_ROLES.includes(userRole)
  const [state, setState] = useState({
    paymentRequired: valetSettings.paymentRequired,
    freeForResidents: valetSettings.freeForResidents,
    requestTips: valetSettings.requestTips,
    // absent from the response until the backend migration lands - keep the
    // checkbox controlled either way
    householdVehicleSharing: !!valetSettings.householdVehicleSharing,
  })

  const handleCheckboxChange = ({ target: { name, checked } }) => {
    setState((prev) => ({ ...prev, [name]: checked }))
  }

  const saveValetSettings = async () => {
    try {
      await API.updateValetSettings({
        buildingId,
        pricing: valetSettings.pricing,
        freeForResidents: state.freeForResidents,
        paymentRequired: state.paymentRequired,
        requestTips: state.requestTips,
        ...(canEditHouseholdVehicleSharing && {
          householdVehicleSharing: state.householdVehicleSharing,
        }),
      })
      displaySnackbar(t('parking-dashboard:notifications.settingsUpdated'))
      fetchValetSettings()
    } catch {
      displaySnackbar(t('common:errors.generic'))
    } finally {
      onClose()
    }
  }

  return (
    <ModalContainer
      additionalClass="parking-settings-modal"
      onClose={onClose}
      headerIcon={
        <Icon icon="cog" svgProps={{ fill: 'none', stroke: 'black' }} viewBox="0 0 24 24" />
      }
      headerTitle={t('parking-dashboard:settingsModal.title')}
    >
      <div className="content-card">
        <div className="settings-row">
          <div className="settings-row--row">
            <CheckboxField
              name="paymentRequired"
              value={state.paymentRequired}
              onChange={handleCheckboxChange}
            />
          </div>
          <div>{t('parking-dashboard:settingsModal.paymentRequired')}</div>
        </div>
        <div className="settings-row">
          <div className="settings-row--row">
            <CheckboxField
              name="freeForResidents"
              value={state.freeForResidents}
              onChange={handleCheckboxChange}
            />
          </div>
          <div>{t('parking-dashboard:settingsModal.freeForResidents')}</div>
        </div>
        <div className="settings-row">
          <div className="settings-row--row">
            <CheckboxField
              name="requestTips"
              value={state.requestTips}
              onChange={handleCheckboxChange}
            />
          </div>
          <div>{t('parking-dashboard:settingsModal.requestTips')}</div>
        </div>
        {canEditHouseholdVehicleSharing && (
          <div className="settings-row">
            <div className="settings-row--row">
              <CheckboxField
                name="householdVehicleSharing"
                value={state.householdVehicleSharing}
                onChange={handleCheckboxChange}
              />
            </div>
            <div>{t('parking-dashboard:settingsModal.householdVehicleSharing')}</div>
          </div>
        )}
      </div>
      <div className="valet-settings-modal-actions">
        <button className="button button-bordered" onClick={onClose}>
          {t('common:actions.cancel')}
        </button>
        <button className="button button-primary" onClick={saveValetSettings}>
          {t('common:actions.save')}
        </button>
      </div>
    </ModalContainer>
  )
}

SettingsModal.propTypes = {
  onClose: PropTypes.func,
  valetSettings: PropTypes.object,
  buildingId: PropTypes.number,
  fetchValetSettings: PropTypes.func,
  userRole: PropTypes.string,
}

export default SettingsModal
